<template>
    <div class="kt-portlet kpi-summary">
        <div class="kt-widget14">
            <div class="kt-widget14__header kt-margin-b-0 kt-padding-b-0">
                <h3 class="kt-widget14__title">KPI</h3>
            </div>
            <div class="kpi-summary-content" v-if="!isLoading && summary != null">
                <!-- <div>
                    <strong>{{ i18n("dashboard.kpiSummary.netSales") }}</strong>
                    <span>{{ netSalesExclTip }}</span>
                </div> -->
                <div>
                    <strong>{{ i18n("dashboard.kpiSummary.tip") }}</strong>
                    <span>{{ netTip }}</span>
                </div>
                <div>
                    <strong>{{ i18n("dashboard.kpiSummary.grossSalesNetExclTip") }}</strong>
                    <span>{{ grossSalesNetExclTip }}</span>
                </div>
                <div>
                    <strong>{{ i18n("dashboard.kpiSummary.netSalesNetExclTip") }}</strong>
                    <span>{{ netSalesNetExclTip }}</span>
                </div>
                <div>
                    <strong>{{ i18n("dashboard.kpiSummary.avgOrder") }}</strong>
                    <span>{{ avgOrder }}</span>
                </div>
                <div>
                    <strong>{{ i18n("dashboard.kpiSummary.avgPayment") }}</strong>
                    <span>{{ avgPayment }}</span>
                </div>
                <!-- <div>
                    <strong>{{ i18n("dashboard.kpiSummary.avgBill") }}</strong>
                    <span>{{ avgBill }}</span>
                </div>
                <div>
                    <strong>{{ i18n("dashboard.kpiSummary.avgBillDurationMins") }}</strong>
                    <span>{{ avgBillDurationMins }}</span>
                </div> -->
            </div>
        </div>

        <div class="loader" v-if="isLoading">
            <span>{{ i18n("general.loading") }}</span>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { Factory } from "@/data/dao/Factory";
    import { DashboardDao } from "@/data/dao/service/DashboardDao";
    import type { IKpiSummary } from "@/data/model/Dashboard";
    import type { DashboardStatsPeriod } from "@/data/model/Enums";
    import { i18n } from "@/portal/Trans";
    import { logError } from "@/shared/logging/errors";
    import { toMoneyWithSign } from "@/shared/utils/format";
    import { computed, onMounted, ref, watch } from "vue";

    const dao = Factory.getInstance<DashboardDao>(DashboardDao);

    const props = defineProps<{
        locationId?: string;
        period?: DashboardStatsPeriod;
        selectedDate?: Date;
    }>();

    const summary = ref<IKpiSummary | undefined>(undefined);
    const isInitialized = ref(false);
    const isLoading = ref(true);
    const paramsLoaded = computed(() => props.locationId !== undefined && props.period !== undefined);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const netTip = computed(() =>
        summary.value?.netTip != undefined ? toMoneyWithSign(summary.value.netTip, true) : "N/A"
    );

    const netSalesNetExclTip = computed(() =>
        summary.value?.netSalesNetExclTip != undefined ? toMoneyWithSign(summary.value.netSalesNetExclTip, true) : "N/A"
    );

    const grossSalesNetExclTip = computed(() =>
        summary.value?.netSalesNetExclTip != undefined
            ? toMoneyWithSign(summary.value.grossSalesNetExclTip, true)
            : "N/A"
    );

    const avgOrder = computed(() =>
        summary.value?.avgOrder != undefined ? toMoneyWithSign(summary.value.avgOrder, true) : "N/A"
    );
    const avgPayment = computed(() =>
        summary.value?.avgPayment != undefined ? toMoneyWithSign(summary.value.avgPayment, true) : "N/A"
    );

    // create chart if all params are provided and is first init or init after not all params were provided
    onMounted(initializeData);
    watch(paramsLoaded, initializeData);

    // reload data if any prop is changed
    watch(() => props.locationId, refreshData);
    watch(() => props.period, refreshData);
    watch(() => props.selectedDate, refreshData);

    async function initializeData() {
        if (!paramsLoaded.value) return;

        loadData();
    }

    async function refreshData() {
        if (!isInitialized.value) return;

        loadData();
    }

    async function loadData() {
        try {
            isLoading.value = true;
            summary.value = undefined;
            summary.value = await dao.getKpiSummary(props.locationId!, props.period!, props.selectedDate);
        } catch (e) {
            logError(e);
        } finally {
            isInitialized.value = true;
            isLoading.value = false;
        }
    }
</script>

<style lang="scss">
    .kpi-summary {
        .kpi-summary-content {
            padding-top: 1em;

            > div {
                display: flex;
                justify-content: space-between;
                margin-bottom: 0.75em;
                padding-left: 0.5em;
                color: #48465b;

                strong {
                    font-weight: 600;
                }
            }
        }

        .loader {
            color: #48465b;
            font-size: 1.1em;
            padding: 0 2em 2em;
            // min-height: 159px;
            min-height: 71px;
        }
    }
</style>
